import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tabs';
import { Check, ImagePlus } from 'lucide-react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@Utils/cn';
import ImageUploadForm from '@Components/ImageUploadForm';
import { useAssets } from '@Hooks/useAssets';
import type { Asset } from '@Types/assets';

interface Props {
  featuredImageId?: string;
  imageIds?: string[];
  /** Persists the change to the draft business object. */
  onUpdate: (patch: { featuredImageId?: string; imageIds?: string[] }) => void;
}

function AssetGrid({
  assets,
  status,
  isSelected,
  onPick,
}: {
  assets: Asset[] | undefined;
  status: string;
  isSelected: (id: string) => boolean;
  onPick: (id: string) => void;
}) {
  return (
    <div className="grid max-h-72 grid-cols-3 gap-3 overflow-y-auto p-1">
      {status === 'pending' ? (
        <>
          <Skeleton className="aspect-square w-full" />
          <Skeleton className="aspect-square w-full" />
          <Skeleton className="aspect-square w-full" />
        </>
      ) : !assets?.length ? (
        <div className="col-span-3 flex h-24 items-center justify-center rounded border border-dashed border-zinc-300 text-sm text-zinc-500">
          No uploaded images yet.
        </div>
      ) : (
        assets.map((asset) => {
          const selected = isSelected(asset.id);
          return (
            <button
              key={asset.id}
              type="button"
              onClick={() => onPick(asset.id)}
              className={cn(
                'relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border bg-zinc-100 transition',
                selected
                  ? 'border-rose-400 ring-2 ring-rose-300'
                  : 'border-transparent hover:border-zinc-300',
              )}
            >
              <CloudinaryImage
                publicId={asset.cloudinaryId}
                mode="original"
                className="max-h-full max-w-full object-contain"
              />
              {selected && (
                <span className="absolute top-1.5 right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-rose-500">
                  <Check className="h-3 w-3 text-white" />
                </span>
              )}
            </button>
          );
        })
      )}
    </div>
  );
}

export default function DraftEventImages({
  featuredImageId,
  imageIds = [],
  onUpdate,
}: Props) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data: assets, status } = useAssets();

  const featuredAsset = assets?.find((a) => a.id === featuredImageId);
  const galleryAssets = assets?.filter((a) => imageIds.includes(a.id)) ?? [];

  const toggleGallery = (id: string) => {
    const next = imageIds.includes(id)
      ? imageIds.filter((x) => x !== id)
      : [...imageIds, id];
    onUpdate({ imageIds: next });
  };

  return (
    <div className="space-y-2">
      {/* Featured thumbnail */}
      <div className="h-24 w-full overflow-hidden rounded-lg bg-zinc-100">
        {featuredAsset ? (
          <CloudinaryImage
            publicId={featuredAsset.cloudinaryId}
            mode="original"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-zinc-400">
            No featured image
          </div>
        )}
      </div>

      {/* Gallery thumbnails */}
      {galleryAssets.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {galleryAssets.map((a) => (
            <div
              key={a.id}
              className="h-10 w-12 overflow-hidden rounded bg-zinc-100"
            >
              <CloudinaryImage
                publicId={a.cloudinaryId}
                mode="original"
                className="h-full w-full object-cover"
              />
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="w-full">
            <ImagePlus className="mr-1.5 h-3.5 w-3.5" />
            {featuredAsset ? 'Manage images' : 'Add images'}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Event images</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="featured">
            <TabsList>
              <TabsTrigger value="featured">Featured</TabsTrigger>
              <TabsTrigger value="gallery">Gallery</TabsTrigger>
              <TabsTrigger value="upload">Upload</TabsTrigger>
            </TabsList>

            <TabsContent value="featured" className="mt-4">
              <p className="mb-2 text-xs text-zinc-500">
                Pick the main image shown for this event.
              </p>
              <AssetGrid
                assets={assets}
                status={status}
                isSelected={(id) => featuredImageId === id}
                onPick={(id) =>
                  onUpdate({
                    featuredImageId: featuredImageId === id ? undefined : id,
                  })
                }
              />
            </TabsContent>

            <TabsContent value="gallery" className="mt-4">
              <p className="mb-2 text-xs text-zinc-500">
                Select additional images for the event gallery.
              </p>
              <AssetGrid
                assets={assets}
                status={status}
                isSelected={(id) => imageIds.includes(id)}
                onPick={toggleGallery}
              />
            </TabsContent>

            <TabsContent value="upload" className="mt-4">
              <ImageUploadForm
                inputId="draft-event-image-upload"
                onUploadSuccess={() =>
                  queryClient.invalidateQueries({ queryKey: ['assets'] })
                }
              />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
